/** How fast the character is assumed to speak while its answer's length is still unknown. */
export const ESTIMATED_CHARACTERS_PER_SECOND = 15;

/**
 * How far the words run ahead of the voice. A word only shows once it is whole,
 * so without a lead the caption reads as lagging behind what is heard.
 */
export const CAPTION_LEAD_MS = 600;

export interface CaptionReveal {
  /** The answer's text, as much of it as has arrived. */
  text: string;
  playedMs: number;
  /** The answer's full audio length, once the answer is complete. */
  totalMs: number | null;
  /** What is already on screen: a caption never takes words back. */
  shownLength: number;
}

/**
 * The part of an answer the user has heard by now, cut after a whole word.
 *
 * Until the answer is complete only the pace is known, so the played time is
 * turned into characters; afterwards the played share of the audio is the same
 * share of the text.
 */
export function revealCaption({
  text,
  playedMs,
  totalMs,
  shownLength,
}: CaptionReveal): string {
  const complete = totalMs !== null;
  const aheadMs = playedMs > 0 ? playedMs + CAPTION_LEAD_MS : 0;
  const heard = complete
    ? text.length * (totalMs > 0 ? Math.min(aheadMs / totalMs, 1) : 1)
    : (aheadMs / 1_000) * ESTIMATED_CHARACTERS_PER_SECOND;
  const target = Math.min(Math.floor(Math.max(heard, shownLength)), text.length);

  if (complete && target === text.length) {
    return text.trimEnd();
  }

  /* A received text can stop halfway through a word, so its end is no boundary. */
  let end = target;
  while (end > 0 && !/\s/.test(text.charAt(end))) {
    end -= 1;
  }

  return text.slice(0, end).trimEnd();
}
